import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import { usePublicClient } from 'wagmi';
import ProgressBar from '../components/ProgressBar';
import StateTimeline from '../components/StateTimeline';
import Fireworks from '../components/Fireworks';
import useBridgeSubmit, { SUBMIT_STATES } from '../hooks/useBridgeSubmit';
import useBridgeDelivery from '../hooks/useBridgeDelivery';
import { decodeChainSwapRequested } from '../lib/bridgeCalldata';
import { INTENT_STASH_KEY } from './BridgeLanding';
import { isStuckState } from '../lib/bridgeApi';
import { recordHistoryEntry } from '../lib/localHistory';
import { formatUnits } from '../lib/wei';
import { chainName, kStableInfo } from '../_shared/wallet-app/chains';
import { BRAND } from '../theme';

// BridgeStatusPage
//
// Fully trustless, non-custodial status tracking — no server intent store.
// Composes two independent sources of truth:
//   1. useBridgeSubmit  — drives the customer's own wallet through
//      approve() + requestSwap() and decodes `swapId` from the mined burn's
//      ChainSwapRequested event.
//   2. useBridgeDelivery — polls the DESTINATION BridgeReceiverUpgradeable's
//      public `fulfilled(swapId)` directly (the bridge_relay daemon is what
//      actually calls fulfillSwap; this page only observes the result).
//
// Reload safety: the burn tx hash is persisted into sessionStorage the
// MOMENT it's known (before confirmation) — see the effect below. On a
// fresh mount, if a burn hash is already on record for this intentId, we
// NEVER re-invoke submit.run() (which would re-sign a brand new
// requestSwap with a fresh block-derived swapId — a genuine double-burn
// risk). Instead we recover swapId by fetching the existing receipt and
// decoding the event from it.

function computePhaseIndex(submitState, bridgeState) {
  if (bridgeState === 'mint_confirmed') return 4;
  if (bridgeState === 'burn_confirmed') return 2;
  if (bridgeState === 'burn_submitted') return 1;
  if (submitState === SUBMIT_STATES.BURNED) return 2;
  if (submitState === SUBMIT_STATES.BURNING) return 1;
  if (submitState === SUBMIT_STATES.APPROVED) return 1;
  if (submitState === SUBMIT_STATES.APPROVING) return 0;
  return 0;
}

function computeFailedIndex(bridgeState) {
  if (!isStuckState(bridgeState)) return null;
  if (bridgeState.includes('mint')) return 3;
  if (bridgeState.includes('relay')) return 2;
  if (bridgeState.includes('burn')) return 1;
  return 1;
}

function readStash(intentId) {
  try {
    const raw = sessionStorage.getItem(INTENT_STASH_KEY);
    if (!raw) return null;
    const j = JSON.parse(raw);
    if (j?.intentId !== intentId) return null;
    return j.intentData || null;
  } catch (_) {
    return null;
  }
}

/** Merge `patch` into the stashed plan for `intentId` — no-op if the stash
 * has moved on to a different intentId (e.g. the user started a new bridge
 * in another tab sharing the same sessionStorage). */
function writeStashPatch(intentId, patch) {
  try {
    const raw = sessionStorage.getItem(INTENT_STASH_KEY);
    const j = raw ? JSON.parse(raw) : null;
    if (!j || j.intentId !== intentId) return;
    j.intentData = { ...(j.intentData || {}), ...patch };
    sessionStorage.setItem(INTENT_STASH_KEY, JSON.stringify(j));
  } catch (_) {
    // sessionStorage may be blocked — reload-resume just won't work; the
    // live in-memory hooks still function for this session.
  }
}

export default function BridgeStatusPage({ intentId, onBack }) {
  const stash = useMemo(() => readStash(intentId), [intentId]);

  const submit = useBridgeSubmit();
  const [submitErrorShown, setSubmitErrorShown] = useState(false);
  const [burnSubmittedAt, setBurnSubmittedAt] = useState(null);
  const [mintConfirmedAt, setMintConfirmedAt] = useState(null);

  // Recovery — populated when a burn hash was already persisted (e.g. a
  // reload mid-flight or after completion in an earlier session) but THIS
  // mount's submit hook never signed it itself.
  const [recovering, setRecovering] = useState(false);
  const [recovered, setRecovered] = useState(null); // { swapId, netAmountWei, reverted }
  const srcPublicClient = usePublicClient({ chainId: Number(stash?.srcChainId) || undefined });

  const persistedBurnTxHash = stash?.burnTxHash || null;
  const effectiveBurnTxHash = submit.burnTxHash || persistedBurnTxHash;
  const effectiveSwapId = submit.swapId || recovered?.swapId || stash?.swapId || null;
  const effectiveNetAmountWei =
    submit.netAmountWei ??
    recovered?.netAmountWei ??
    (stash?.netAmountWei ? BigInt(stash.netAmountWei) : null);

  // Kick off approve + requestSwap ONLY on a genuinely fresh submission —
  // never if a burn tx hash is already on record for this plan.
  useEffect(() => {
    if (!stash) return;
    if (persistedBurnTxHash) return;
    if (submit.state !== SUBMIT_STATES.IDLE) return;
    submit.run({
      srcChainId: Number(stash.srcChainId),
      srcKStableAddress: stash.srcKStableAddress,
      srcBridgeSenderAddress: stash.srcBridgeSenderAddress,
      approvalCalldata: stash.approvalCalldata,
      bridgeCalldata: stash.bridgeCalldata,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stash, persistedBurnTxHash, submit.state]);

  // Persist the burn hash the MOMENT it exists (before confirmation) — this
  // is what makes a mid-flight reload safe.
  useEffect(() => {
    if (submit.burnTxHash && intentId) {
      writeStashPatch(intentId, { burnTxHash: submit.burnTxHash });
      setBurnSubmittedAt((prev) => prev ?? Math.floor(Date.now() / 1000));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submit.burnTxHash, intentId]);

  // Persist swapId/netAmount as soon as this session decodes them.
  useEffect(() => {
    if (submit.swapId && intentId) {
      writeStashPatch(intentId, {
        swapId: submit.swapId,
        netAmountWei: submit.netAmountWei != null ? submit.netAmountWei.toString() : undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submit.swapId, submit.netAmountWei, intentId]);

  // Recovery path: a burn hash is persisted but this session never signed it
  // itself. Fetch the existing receipt and decode from it — NEVER resend.
  useEffect(() => {
    if (!stash || !persistedBurnTxHash) return;
    if (submit.burnTxHash) return; // this session did the signing — no recovery needed
    if (recovered || recovering) return;
    if (!srcPublicClient) return;
    let cancelled = false;
    setRecovering(true);
    (async () => {
      try {
        const receipt = await srcPublicClient.getTransactionReceipt({ hash: persistedBurnTxHash });
        if (cancelled) return;
        if (receipt.status !== 'success') {
          setRecovered({ swapId: null, netAmountWei: null, reverted: true });
          return;
        }
        const args = decodeChainSwapRequested(receipt, stash.srcBridgeSenderAddress);
        const rec = {
          swapId: stash.swapId || args?.swapId || null,
          netAmountWei: args?.netAmount ?? (stash.netAmountWei ? BigInt(stash.netAmountWei) : null),
          reverted: false,
        };
        setRecovered(rec);
        if (rec.swapId) {
          writeStashPatch(intentId, {
            swapId: rec.swapId,
            netAmountWei: rec.netAmountWei != null ? rec.netAmountWei.toString() : undefined,
          });
        }
      } catch (_) {
        // Receipt not found yet (still pending) — leave recovered null; a
        // manual page refresh re-tries. We deliberately don't loop here.
      } finally {
        if (!cancelled) setRecovering(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [stash, persistedBurnTxHash, submit.burnTxHash, recovered, recovering, srcPublicClient, intentId]);

  const delivery = useBridgeDelivery(stash?.dstChainId, stash?.dstBridgeReceiverAddress, effectiveSwapId);

  useEffect(() => {
    if (delivery.fulfilled) setMintConfirmedAt((prev) => prev ?? Math.floor(Date.now() / 1000));
  }, [delivery.fulfilled]);

  const burnReverted = recovered?.reverted === true;

  const bridgeState = delivery.fulfilled
    ? 'mint_confirmed'
    : burnReverted
      ? 'stuck_burn_reverted'
      : delivery.stuck
        ? 'stuck_relay'
        : effectiveBurnTxHash && (submit.state === SUBMIT_STATES.BURNED || recovered || persistedBurnTxHash)
          ? 'burn_confirmed'
          : effectiveBurnTxHash
            ? 'burn_submitted'
            : 'intent_created';

  const phaseIdx = computePhaseIndex(submit.state, bridgeState);
  const failedIdx =
    computeFailedIndex(bridgeState) ?? (submit.state === SUBMIT_STATES.ERROR && !effectiveBurnTxHash ? phaseIdx : null);
  const done = bridgeState === 'mint_confirmed';

  const [showFireworks, setShowFireworks] = useState(false);
  useEffect(() => {
    if (done) setShowFireworks(true);
  }, [done]);

  // Keep this browser's local history entry in sync as the bridge progresses
  // (see lib/localHistory.js — there is no server-side history in the live
  // architecture, so BridgeHistoryPage reads this).
  useEffect(() => {
    if (!intentId) return;
    recordHistoryEntry({
      localId: intentId,
      state: bridgeState,
      burnTxHash: effectiveBurnTxHash || undefined,
      swapId: effectiveSwapId || undefined,
      mintTxHash: delivery.mintTxHash || undefined,
    });
  }, [intentId, bridgeState, effectiveBurnTxHash, effectiveSwapId, delivery.mintTxHash]);

  const symbol = stash?.kStableSymbol || '';
  const info = kStableInfo(symbol);
  const decimals = stash?.decimals ?? info?.decimals ?? 6;
  const displayAmountWei = stash?.amountWei ? BigInt(stash.amountWei) : 0n;
  const displayNetWei = effectiveNetAmountWei ?? (stash?.netWei ? BigInt(stash.netWei) : 0n);

  const status = {
    state: bridgeState,
    intentCreatedAt: stash ? Math.floor(stash.createdAt / 1000) : undefined,
    srcChainId: stash?.srcChainId,
    dstChainId: stash?.dstChainId,
    burnTxHash: effectiveBurnTxHash,
    burnSubmittedAt,
    burnConfirmedAt: burnSubmittedAt,
    mintTxHash: delivery.mintTxHash,
    mintConfirmedAt,
  };

  // Only offer a real resubmit (new signature) when NO burn tx exists yet.
  // Once a burn hash is on record — signed by this session or recovered
  // from a reload — Retry only dismisses the banner; it never re-signs
  // (that would risk a genuine second on-chain burn).
  const canResubmit = !effectiveBurnTxHash;
  const onRetryClick = () => {
    if (!canResubmit) {
      setSubmitErrorShown(true);
      return;
    }
    submit.run({
      srcChainId: Number(stash?.srcChainId),
      srcKStableAddress: stash?.srcKStableAddress,
      srcBridgeSenderAddress: stash?.srcBridgeSenderAddress,
      approvalCalldata: stash?.approvalCalldata,
      bridgeCalldata: stash?.bridgeCalldata,
    });
  };

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto' }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        sx={{ mb: 2, color: BRAND.textSecondary }}
      >
        New bridge
      </Button>

      <Card data-testid="bridge-status-card">
        <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="caption" sx={{ color: BRAND.textSecondary, letterSpacing: '0.06em' }}>
                BRIDGE
              </Typography>
              <Typography
                variant="h5"
                sx={{ fontFamily: 'monospace', wordBreak: 'break-all', color: BRAND.textPrimary }}
                data-testid="intent-id-display"
              >
                {intentId}
              </Typography>
              {stash && (
                <Typography variant="body2" sx={{ color: BRAND.textSecondary, mt: 0.5 }}>
                  {formatUnits(displayAmountWei, decimals)} {symbol}
                  {' · '}
                  {chainName(stash.srcChainId)} → {chainName(stash.dstChainId)}
                </Typography>
              )}
            </Box>

            <ProgressBar currentIndex={phaseIdx} failedIndex={failedIdx} />

            {submit.state === SUBMIT_STATES.APPROVING && (
              <Alert severity="info" icon={<CircularProgress size={16} />}>
                Approving the bridge to spend your {symbol}. Confirm in your wallet.
              </Alert>
            )}
            {submit.state === SUBMIT_STATES.BURNING && (
              <Alert severity="info" icon={<CircularProgress size={16} />}>
                Signing the burn that emits the cross-chain message. Confirm in your wallet.
              </Alert>
            )}
            {submit.state === SUBMIT_STATES.ERROR && !submitErrorShown && (
              <Alert
                severity="error"
                onClose={() => setSubmitErrorShown(true)}
                action={
                  <Button color="inherit" size="small" startIcon={<SendIcon />} onClick={onRetryClick}>
                    {canResubmit ? 'Retry' : 'Dismiss'}
                  </Button>
                }
              >
                {submit.error || 'Transaction failed.'}
              </Alert>
            )}
            {burnReverted && (
              <Alert severity="error">
                The burn transaction reverted on-chain — no funds were bridged. Please
                start a new bridge from the landing page.
              </Alert>
            )}
            {recovering && !effectiveSwapId && (
              <Alert severity="info" icon={<CircularProgress size={16} />}>
                Recovering bridge status from the burn transaction…
              </Alert>
            )}
            {done && (
              <Alert severity="success" data-testid="success-alert">
                You hold {formatUnits(displayNetWei, decimals)} {symbol} on{' '}
                {chainName(stash?.dstChainId)}.
              </Alert>
            )}

            <Divider sx={{ borderColor: BRAND.border }} />

            <Box>
              <Typography
                variant="overline"
                sx={{ color: BRAND.textSecondary, letterSpacing: '0.1em' }}
              >
                Activity
              </Typography>
              <Box sx={{ mt: 1 }}>
                {!stash ? (
                  <Alert severity="warning">
                    Could not find this bridge's details in this browser. If you just
                    started it, go back and try again.
                  </Alert>
                ) : (
                  <StateTimeline status={status} />
                )}
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Fireworks show={showFireworks} onDone={() => setShowFireworks(false)} />
    </Box>
  );
}
